import React from "react";
import { Modal } from "../../components";
import PanelSegmentType from "../../components/panels/PanelSegment/PanelSegment";
import { useCustomQuery, useManualPagination } from "../../utilities/hookutils";
import RecipientsList from "../components/Recipients/RecipientsList";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import SelectContact from "components/AddressBook/SelectContact";
import EditParticipantAsContact from "eSign/components/modals/EditParticipantAsContact";
import { useMutation } from "@tanstack/react-query";
import { SetParticipantFromContactParams } from "eSign/components/Participant/hooks";
import { useShortcutPanel } from "components/Shortcuts/hooks";

interface Props {
  initialViewers: server.dto.ApiResponseWrapper<server.dto.InvoiceParticipant[]>
  invoice: server.dto.InvoiceV2
}

export const recipientsQueryKey = "invoiceRecipients";
const RecipientsPanel = (props: Props) => {
  const [displayAddViewer, setDisplayAddViewer] = React.useState(false);
  const [contact, setContact] = React.useState<{
    contactId: number
    contactName: string
    culture: server.dto.Culture
    email: string
  }>(undefined);

  const [listInstruction, setListInstruction] = React.useState<server.dto.ListInvoiceParticipantInstruction>({
    page: props.initialViewers.pagination.page,
    perPage: serverReference.constants.eSignParticipant.viewersPageSize,
    invoiceId: props.invoice.invoiceId
  });

  const participantsQuery = useCustomQuery({
    queryFn: async () => window.CoreApi.InvoiceV2.ListParticipants(listInstruction).then(r => r.data),
    queryKey: [recipientsQueryKey, listInstruction],
    initialData: props.initialViewers,
    onSuccess: (res) => {
      pagination.setPagination(res.pagination);
    }
  });

  const pagination = useManualPagination(1, serverReference.constants.eSignParticipant.viewersPageSize, (pagination) => {
    setListInstruction(prevInstr => ({
      ...prevInstr,
      page: pagination.page,
      perPage: pagination.perPage
    }));
  });

  const onAddClicked = () => {
    setDisplayAddViewer(true);
  };

  const saveViewerMutation = useMutation({
    mutationFn: async ({ email, contactId, culture, invitationBody, invitationSubject }: SetParticipantFromContactParams) => {
      const res = await window.CoreApi.InvoiceV2.AddParticipant({
        invoiceId: props.invoice.invoiceId,
        contactId,
        emailAddress: email,
        contactCulture: culture,
        invitationBody,
        invitationSubject,
      });

      if (!res.data.hasErrors) {
        await participantsQuery.refetch();
        setDisplayAddViewer(false);
        return;
      }

      return res.data.errors[0].message;
    },
    onSuccess: () => {
      setContact(undefined);
    }
  })

  const onContactSelected = async (contact: server.dto.ContactAddressBookEntry, culture: server.dto.Culture, email: string) => {
    setContact({
      contactId: contact.contactId,
      contactName: contact.name.fullName,
      culture,
      email
    });
  };

  const onCloseModal = () => {
    setContact(undefined);
    setDisplayAddViewer(false);
  };

  const cannotAddMoreViewers = pagination.total >= serverReference.constants.eSignParticipant.maxViewersLimit;

  const lifeCyclesAllowedToAddViewers: server.dto.DocumentLifeCycles[] = ["draft", "review", "pending"];
  const canAddViewers = lifeCyclesAllowedToAddViewers.includes(props.invoice.lifeCycle);

  const shortcuts = useShortcutPanel({
    type: "recipient",
    label: RESX.Recipient.Recipients,
    totalItems: pagination.total,
    onAddClicked: canAddViewers ? onAddClicked : undefined,
  });

  return <SectionPanel.Panel
    panelRef={shortcuts.refToScrollTo}
    id={"recipient-list"}
    header={<>
      <PanelSegmentType.Title title={RESX.Recipient.Recipients} />

      {canAddViewers && <PanelSegmentType.Button
        onClick={onAddClicked}
        disabled={cannotAddMoreViewers}
        type="secondary"
        label={RESX.Buttons.Add} />}
      {pagination.Component}
    </>}
  >
    <RecipientsList
      invoice={props.invoice}
      viewers={participantsQuery.data.data}
      onChanged={async () => participantsQuery.refetch()}
    />

    {(displayAddViewer && contact == null) && <Modal
      isOpen
      handleClose={onCloseModal}
      title={RESX.Recipient.AddRecipient}
      width="small"
      height={750}
    >
      <SelectContact
        hasNoCompany
        suggestedApiInstruction={{ itemId: props.invoice.invoiceId, mainArea: "invoice" }}
        onContactSelected={onContactSelected}
      />
    </Modal>}

    {(displayAddViewer && contact != null) && <Modal
      isOpen
      handleClose={onCloseModal}
      title={RESX.Recipient.AddRecipient}
      width="small"
    >
      {contact != null && <EditParticipantAsContact
        contactName={contact.contactName}
        contactId={contact.contactId}
        initialEmail={contact.email}
        initialCompany={null}
        initialCulture={contact.culture}
        initialInvitationBody=""
        initialInvitationSubject=""
        saveMutation={saveViewerMutation}
        isViewer
        onPreviousStepClicked={() => { setContact(undefined); }}
        messageTemplateType={"invitationForInvoiceV2"}
      />}
    </Modal>}

  </SectionPanel.Panel>
};

export default RecipientsPanel;